import type { FailureReason, FiatStepResponse } from '@funkit/connect-core'
import type { TFunction } from 'i18next'
import { Alert } from 'react-native'
import { isAboveMaximum } from '../hooks/useGuardedAmountInput'
import { openKycSupport } from './kycSupport'

/**
 * What the alert says when the flow is sent back to the quote with an error.
 *
 * The server answers a refusal it cannot place on a field — a provider that
 * will not fill the amount, a form the provider no longer holds, an outage —
 * by re-presenting the quote with `error` set. Nothing on the amount screen
 * shows that, so without this the user watches the form they just filled
 * close and is left on the amount with no word why (design 2114:8131: the
 * default iOS alert over the deposit screen).
 */
export interface QuoteRefusalAlert {
  title: string
  message: string
}

export function showPaymentUnavailableAlert(
  t: TFunction,
  applePay: boolean,
  scheme?: 'light' | 'dark',
) {
  Alert.alert(
    applePay
      ? t('fiat.applePayUnavailableTitle')
      : t('fiat.refusalUnavailableTitle'),
    t('fiat.refusalUnavailable'),
    [
      { text: t('common.ok'), isPreferred: true },
      { text: t('fiat.contactSupport'), onPress: openKycSupport },
    ],
    { userInterfaceStyle: scheme },
  )
}

export function showQuoteRefusalAlert(
  error: FailureReason,
  t: TFunction,
  scheme?: 'light' | 'dark',
) {
  const { title, message } = quoteRefusalAlert(error, t)
  const unavailable = error.category !== 'USER_ERROR'
  Alert.alert(
    title,
    message,
    unavailable
      ? [
          { text: t('common.ok'), isPreferred: true },
          { text: t('fiat.contactSupport'), onPress: openKycSupport },
        ]
      : [{ text: t('common.ok') }],
    { userInterfaceStyle: scheme },
  )
}

/** The error a step response carries back onto the quote, if it does. */
export function quoteRefusal(
  stepResponse: FiatStepResponse | undefined,
): FailureReason | undefined {
  const state = stepResponse?.state
  return state?.kind === 'QUOTE' ? state.error : undefined
}

/**
 * A refusal for asking to spend more than this buyer may.
 *
 * It belongs on the amount field, not in an alert. The server re-presents the
 * quote with `limits.max` narrowed to the cap that refused, so the screen names
 * the number and offers the way down to it — the same answer every other
 * maximum gets (ENG-6302). An alert on top of that says it twice, in wording
 * that is in none of the designs, and leaves the buyer to dismiss it before
 * they can act on the ceiling underneath.
 */
export function isOverLimit(error: FailureReason): boolean {
  return (
    error.code === 'LIMITS_EXCEEDED' ||
    error.code === 'LIMITS_EXCEEDED_UPGRADEABLE'
  )
}

/**
 * Whether the screen can actually name the cap an over-limit refusal carries.
 *
 * `isOverLimit` suppresses the alert because the amount field shows the ceiling
 * instead, and the contract makes `limits.max` required on a `QUOTE`, so a
 * ceiling is there to show. But the suppression and the recovery read different
 * fields, and nothing in the types ties them together: a server that answered
 * with a zero or unparseable max would silence the alert and leave the field
 * offering the range that was just refused — no alert and no number.
 *
 * So the invariant is checked rather than trusted. Staying silent is only right
 * when there is something to be silent about.
 */
export function hasOverLimitCeiling(
  stepResponse: FiatStepResponse | undefined,
  refusedMaximum: { amount: string } | undefined,
  amount: string,
): boolean {
  // Under the entered amount, not merely present: a cap equal to what they typed
  // draws no bound notice and no "Change to maximum", so silence would leave the
  // buyer staring at the same figure with nothing to act on.
  const actionable = (cap: string | undefined) => {
    if (cap === undefined) {
      return false
    }
    const parsed = Number(cap)
    return Number.isFinite(parsed) && parsed > 0 && isAboveMaximum(amount, cap)
  }
  if (actionable(refusedMaximum?.amount)) {
    return true
  }
  const state = stepResponse?.state
  return state?.kind === 'QUOTE' && actionable(state.limits?.max)
}

export function quoteRefusalAlert(
  error: FailureReason,
  t: TFunction,
): QuoteRefusalAlert {
  // `code` is the contract's copy key, so it is what the wording keys off.
  if (error.code === 'OTP_DISPATCH_THROTTLED') {
    return {
      title: t('fiat.otpRateLimitedTitle'),
      message: t('fiat.otpRateLimited'),
    }
  }
  // Reached only when the field could not name the cap. "Check what you entered"
  // would send them back over their phone number and name when the amount is what
  // was refused, so an over-limit refusal keeps amount-worded copy of its own.
  if (isOverLimit(error)) {
    return {
      title: t('fiat.refusalLimitsTitle'),
      message: t('fiat.refusalLimits'),
    }
  }
  if (error.category === 'USER_ERROR') {
    return {
      title: t('fiat.refusalDetailsTitle'),
      message: t('fiat.refusalDetails'),
    }
  }
  return {
    title: t('fiat.refusalUnavailableTitle'),
    message: t('fiat.refusalUnavailable'),
  }
}

/** Select the contact-screen recovery, if the refusal belongs on that screen. */
export function contactRefusal(
  error: FailureReason | undefined,
): 'reverify' | 'change' | 'rate-limited' | undefined {
  if (error?.code === 'PHONE_REVERIFY') {
    return 'reverify'
  }
  if (error?.code === 'OTP_DISPATCH_THROTTLED') {
    return 'rate-limited'
  }
  if (error?.category !== 'USER_ERROR' || isOverLimit(error)) {
    return undefined
  }
  return 'change'
}
